import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { APP_SHORTCUT_EVENT } from "../utils/shortcuts";

const SIZE_OPTIONS = ["small", "medium", "large"];
const GST_OPTIONS = ["both", "separate", "none"];

const DEFAULTS = {
  printerWidth: "2in",
  printLogoOnBill: true,
  printUpiQrOnBill: true,
  alwaysPrintUpiQr: false,
  printTokenNo: true,
  printGoogleReviewQr: false,
  logoSize: "medium",
  upiQrSize: "large",
  businessNameSize: "medium",
  totalAmountSize: "medium",
  savingAmountSize: "medium",
  googleReviewQrSize: "small",
  gstPrintFormat: "both",
  restaurantNameSize: "small",
  customerNameSize: "small",
};

const readBool = (key, fallback) => {
  const raw = localStorage.getItem(key);
  if (raw === null) return fallback;
  return raw === "true";
};

const readString = (key, fallback) => localStorage.getItem(key) || fallback;

const PrinterSettingsPage = () => {
  const [feedback, setFeedback] = useState(null);
  const saveRef = useRef(null);
  const [settings, setSettings] = useState(() => ({
    printerWidth: readString("printerWidth", DEFAULTS.printerWidth),
    printLogoOnBill: readBool("printer_setting_printLogoOnBill", DEFAULTS.printLogoOnBill),
    printUpiQrOnBill: readBool("printer_setting_printUpiQrOnBill", DEFAULTS.printUpiQrOnBill),
    alwaysPrintUpiQr: readBool("printer_setting_alwaysPrintUpiQr", DEFAULTS.alwaysPrintUpiQr),
    printTokenNo: readBool("printer_setting_printTokenNo", DEFAULTS.printTokenNo),
    printGoogleReviewQr: readBool("printer_setting_printGoogleReviewQr", DEFAULTS.printGoogleReviewQr),
    logoSize: readString("printer_setting_logoSize", DEFAULTS.logoSize),
    upiQrSize: readString("printer_setting_upiQrSize", DEFAULTS.upiQrSize),
    businessNameSize: readString("printer_setting_businessNameSize", DEFAULTS.businessNameSize),
    totalAmountSize: readString("printer_setting_totalAmountSize", DEFAULTS.totalAmountSize),
    savingAmountSize: readString("printer_setting_savingAmountSize", DEFAULTS.savingAmountSize),
    googleReviewQrSize: readString("printer_setting_googleReviewQrSize", DEFAULTS.googleReviewQrSize),
    gstPrintFormat: readString("printer_setting_gstPrintFormat", DEFAULTS.gstPrintFormat),
    restaurantNameSize: readString("pos_restaurant_name_size", DEFAULTS.restaurantNameSize),
    customerNameSize: readString("pos_customer_name_size", DEFAULTS.customerNameSize),
  }));

  const setValue = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));
  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback(null), 2200);
  };

  const save = () => {
    localStorage.setItem("printerWidth", settings.printerWidth);
    localStorage.setItem("printer_width", settings.printerWidth);
    localStorage.setItem("printer_setting_printLogoOnBill", String(settings.printLogoOnBill));
    localStorage.setItem("printer_setting_printUpiQrOnBill", String(settings.printUpiQrOnBill));
    localStorage.setItem("printer_setting_alwaysPrintUpiQr", String(settings.alwaysPrintUpiQr));
    localStorage.setItem("printer_setting_printTokenNo", String(settings.printTokenNo));
    localStorage.setItem("printer_setting_printGoogleReviewQr", String(settings.printGoogleReviewQr));
    localStorage.setItem("printer_setting_logoSize", settings.logoSize);
    localStorage.setItem("printer_setting_upiQrSize", settings.upiQrSize);
    localStorage.setItem("printer_setting_businessNameSize", settings.businessNameSize);
    localStorage.setItem("printer_setting_totalAmountSize", settings.totalAmountSize);
    localStorage.setItem("printer_setting_savingAmountSize", settings.savingAmountSize);
    localStorage.setItem("printer_setting_googleReviewQrSize", settings.googleReviewQrSize);
    localStorage.setItem("printer_setting_gstPrintFormat", settings.gstPrintFormat);
    localStorage.setItem("pos_restaurant_name_size", settings.restaurantNameSize);
    localStorage.setItem("pos_customer_name_size", settings.customerNameSize);
    toast.success("Printer settings saved");
    showFeedback("success", "Printer settings saved");
  };
  saveRef.current = save;

  const reset = () => {
    setSettings(DEFAULTS);
    Object.entries(DEFAULTS).forEach(([key, value]) => {
      const map = {
        printerWidth: ["printerWidth", "printer_width"],
        printLogoOnBill: ["printer_setting_printLogoOnBill"],
        printUpiQrOnBill: ["printer_setting_printUpiQrOnBill"],
        alwaysPrintUpiQr: ["printer_setting_alwaysPrintUpiQr"],
        printTokenNo: ["printer_setting_printTokenNo"],
        printGoogleReviewQr: ["printer_setting_printGoogleReviewQr"],
        logoSize: ["printer_setting_logoSize"],
        upiQrSize: ["printer_setting_upiQrSize"],
        businessNameSize: ["printer_setting_businessNameSize"],
        totalAmountSize: ["printer_setting_totalAmountSize"],
        savingAmountSize: ["printer_setting_savingAmountSize"],
        googleReviewQrSize: ["printer_setting_googleReviewQrSize"],
        gstPrintFormat: ["printer_setting_gstPrintFormat"],
        restaurantNameSize: ["pos_restaurant_name_size"],
        customerNameSize: ["pos_customer_name_size"],
      };
      (map[key] || []).forEach((storageKey) => localStorage.setItem(storageKey, String(value)));
    });
    toast.success("Printer settings reset");
    showFeedback("success", "Printer settings reset");
  };

  const toggles = useMemo(
    () => [
      ["Print logo on bill", "printLogoOnBill", "Restaurant logo on top of printed bill"],
      ["Print UPI QR on bill", "printUpiQrOnBill", "Customer can scan and pay directly"],
      ["Always print UPI QR", "alwaysPrintUpiQr", "Show UPI QR even when payment mode is cash/card"],
      ["Print token number", "printTokenNo", "Include KOT/order token on bill"],
      ["Print Google review QR", "printGoogleReviewQr", "Show review QR at bill footer"],
    ],
    []
  );

  useEffect(() => {
    const onShortcut = (event) => {
      if (event?.detail?.id !== "printer.save") return;
      event.preventDefault();
      saveRef.current?.();
    };

    window.addEventListener(APP_SHORTCUT_EVENT, onShortcut);
    return () => window.removeEventListener(APP_SHORTCUT_EVENT, onShortcut);
  }, []);

  return (
    <Page>
      <Header>
        <h1>Printer Settings</h1>
        <p>These settings are used by web printing and thermal print output.</p>
      </Header>

      <Card>
        <h3>Thermal Paper Width</h3>
        <Row>
          <Select
            value={settings.printerWidth}
            onChange={(e) => setValue("printerWidth", e.target.value)}
          >
            <option value="2in">2 inch</option>
            <option value="3in">3 inch</option>
            <option value="4in">4 inch</option>
            <option value="a4">A4</option>
          </Select>
        </Row>
      </Card>

      <Card>
        <h3>Bill Options</h3>
        {toggles.map(([label, key, hint]) => (
          <ToggleRow key={key} $active={settings[key]}>
            <ToggleText>
              <strong>{label}</strong>
              <small>{hint}</small>
            </ToggleText>
            <Switch>
              <HiddenCheckbox
                type="checkbox"
                checked={settings[key]}
                onChange={(e) => setValue(key, e.target.checked)}
              />
              <Slider $checked={settings[key]} />
            </Switch>
          </ToggleRow>
        ))}
      </Card>

      <Card>
        <h3>Size Preferences</h3>
        <Field>
          <label>Logo size</label>
          <Select value={settings.logoSize} onChange={(e) => setValue("logoSize", e.target.value)}>
            {SIZE_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </Field>
        <Field>
          <label>UPI QR size</label>
          <Select value={settings.upiQrSize} onChange={(e) => setValue("upiQrSize", e.target.value)}>
            {SIZE_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </Field>
        <Field>
          <label>Restaurant name size</label>
          <Select value={settings.restaurantNameSize} onChange={(e) => setValue("restaurantNameSize", e.target.value)}>
            <option value="small">small</option>
            <option value="large">large</option>
          </Select>
        </Field>
        <Field>
          <label>Customer name size</label>
          <Select value={settings.customerNameSize} onChange={(e) => setValue("customerNameSize", e.target.value)}>
            <option value="small">small</option>
            <option value="large">large</option>
          </Select>
        </Field>
        <Field>
          <label>GST print format</label>
          <Select value={settings.gstPrintFormat} onChange={(e) => setValue("gstPrintFormat", e.target.value)}>
            {GST_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </Field>
      </Card>

      <Actions>
        {feedback ? (
          <InlineNotice $type={feedback.type}>{feedback.message}</InlineNotice>
        ) : null}
        <Btn type="button" onClick={reset}>Reset</Btn>
        <Btn type="button" $primary onClick={save}>Save</Btn>
      </Actions>
    </Page>
  );
};

export default PrinterSettingsPage;

const Page = styled.div`
  min-height: 100vh;
  padding: 16px;
  background: #f3f5fb;
  display: grid;
  gap: 12px;
`;

const Header = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
  h1 { margin: 0; font-size: 1.4rem; }
  p { margin: 6px 0 0; color: #64748b; }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
  h3 { margin: 0 0 12px; }
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid ${({ $active }) => ($active ? "rgba(1,91,154,.34)" : "#e2e8f0")};
  border-radius: 12px;
  background: ${({ $active }) =>
    $active ? "linear-gradient(180deg,#f3f9ff,#ffffff)" : "#ffffff"};
  margin-bottom: 10px;
`;

const ToggleText = styled.div`
  display: grid;
  gap: 3px;
  strong {
    color: #0f172a;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.2;
  }
  small {
    color: #64748b;
    font-size: 12px;
    line-height: 1.25;
  }
`;

const Switch = styled.label`
  position: relative;
  width: 50px;
  height: 28px;
  display: inline-block;
  flex-shrink: 0;
  cursor: pointer;
`;

const HiddenCheckbox = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
`;

const Slider = styled.span`
  position: absolute;
  inset: 0;
  border-radius: 999px;
  background: ${({ $checked }) => ($checked ? "#015b9a" : "#cbd5e1")};
  transition: background 0.2s ease;

  &::before {
    content: "";
    position: absolute;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #fff;
    top: 3px;
    left: ${({ $checked }) => ($checked ? "25px" : "3px")};
    box-shadow: 0 3px 8px rgba(15, 23, 42, 0.26);
    transition: left 0.2s ease;
  }
`;

const Field = styled.div`
  display: grid;
  gap: 6px;
  margin-bottom: 10px;
  label { font-size: 13px; color: #334155; }
`;

const Select = styled.select`
  height: 38px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  padding: 0 10px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const InlineNotice = styled.div`
  margin-right: auto;
  padding: 8px 12px;
  border-radius: 10px;
  font-weight: 800;
  border: 1px solid ${({ $type }) => ($type === "success" ? "#86efac" : "#fecaca")};
  color: ${({ $type }) => ($type === "success" ? "#166534" : "#991b1b")};
  background: ${({ $type }) => ($type === "success" ? "#f0fdf4" : "#fef2f2")};
`;

const Btn = styled.button`
  border: 1px solid ${({ $primary }) => ($primary ? "#015b9a" : "#cbd5e1")};
  background: ${({ $primary }) => ($primary ? "#015b9a" : "#fff")};
  color: ${({ $primary }) => ($primary ? "#fff" : "#0f172a")};
  border-radius: 10px;
  height: 40px;
  padding: 0 14px;
  font-weight: 700;
  cursor: pointer;
`;
